/**
 * TRAC-848: the chat-export data model.
 *
 * A typed, renderer-agnostic projection of `chatStore.messages` into the
 * chronologically interleaved record we hand the customer. All four export
 * formats (Markdown, self-contained HTML, PDF, and the CSV companion sheet)
 * render THIS — never `Message[]` directly. One projection, four renderers:
 * the interleaving is decided once, here, and a format can only differ in how
 * it draws a block, not in which blocks exist.
 *
 * Two things this model deliberately does NOT do:
 *  - It does not decide where the "prices as of" stamp renders. It carries
 *    `capturedAt` per tire and `priceWindow` for the whole export, so a renderer
 *    can stamp per-card, once in a header, or both. That call is still open.
 *  - It does not silently drop anything. A part type it doesn't recognize
 *    becomes a visible `omitted` block AND flips completeness to `partial`.
 *    Transient UI affordances are a separate, deliberate `dropped` set below —
 *    those are not holes and do not affect completeness.
 */

import type { Message, MessagePart, TireInfo, RatingMetric } from '../../types/chat.types';

/** Who produced a turn, from the customer's point of view. */
export type ExportSpeaker = 'customer' | 'traci' | 'human-agent' | 'system';

/**
 * A tire exactly as it was shown at that point in the conversation — a snapshot,
 * not a live lookup. `capturedAt` is the timestamp of the turn that presented it,
 * which is what makes the "as of" stamp honest when a long conversation spans a
 * price change.
 */
export interface TireSnapshot {
  manufacturer?: string;
  modelName?: string;
  tireType?: string;
  productUrl?: string;
  /** Display price, mirroring MessageRenderer's `tire.price || tire.priceRange`. */
  price?: string;
  /** Which of the two the price came from — CSV and per-card stamps need to distinguish. */
  priceKind: 'exact' | 'range' | 'unavailable';
  /**
   * RAW, on the 10-point scale it is stored in. The UI shows it halved beside
   * five stars, so anything customer-facing must go through
   * `tireRatingOutOfFive`/`ratingLine` rather than printing this directly.
   */
  overallRating?: number;
  reviewCount?: number;
  ratingMetrics?: RatingMetric[];
  /** Loosely-typed passthrough; the CSV companion picks columns from this. */
  specs?: Record<string, unknown>;
  capturedAt: Date;
}

export type ExportBlock =
  /** Prose the customer reads. */
  | { kind: 'prose'; text: string }
  /**
   * One or more tires presented together. Consecutive `tire_card` parts are
   * coalesced here for the same reason MessageRenderer groups them into a grid
   * (MessageRenderer.tsx ~995): they were presented as one set, so the export
   * should read as one set.
   */
  | { kind: 'tires'; tires: TireSnapshot[] }
  /**
   * A destination we sent the customer to (a size search, a category page, TDG
   * results, a guide page). Carries `eyebrow` and `cta` because the chat's shared
   * LinkCard shows all three — a small uppercase descriptor, the prominent label,
   * and a CTA button. Reducing it to a bare link loses what the card said it was.
   */
  | { kind: 'link'; label: string; url: string; eyebrow?: string; cta?: string; blurb?: string }
  /**
   * The personalized-recommendations (TDG) card. Distinct from a plain `link`
   * because the chat gives it its own prominent treatment — a teal-bordered card
   * headed by the vehicle it was built for, with a size/season detail row.
   * Flattening it into a generic link loses the "built for YOUR car" framing that
   * is the entire reason the customer would keep this link.
   */
  | {
      kind: 'recommendations';
      title: string;
      vehicle?: string;
      details: { label: string; value: string }[];
      /**
       * The priorities the recommendation was weighted toward. TDGLinkCard shows
       * these as "Current Priorities" and derives them from the URL's attribute
       * weights (>= 3 counts as on) — so they survive persistence and belong in
       * the export. What does NOT survive is a customer's in-session chip toggles:
       * those live in component state (`modifiedPriorities`) and are never written
       * back to the part, so a reload — and therefore any export — sees the card
       * as it was delivered.
       */
      priorities: { label: string; on: boolean }[];
      /** The season options, with the one this recommendation used marked. */
      tireTypes: { label: string; on: boolean }[];
      indicators: { label: string; description?: string }[];
      url: string;
      cta: string;
    }
  /**
   * The sources behind an answer — tire tests, product pages. Each carries its
   * real URL: `source_cards` options put the destination in `value` and the kind
   * ("Tire Test", "Product Page") in `subtitle`. Dropping those URLs would gut
   * the feature, whose whole promise is a record with working links.
   */
  | { kind: 'sources'; title?: string; items: { label: string; url: string; kind?: string }[] }
  /** A vehicle/size the conversation established. */
  | { kind: 'vehicle'; description?: string; tireSize?: string; frontSize?: string; rearSize?: string; staggered?: boolean }
  /**
   * A standalone rating readout not attached to a tire card.
   *
   * `outOf` is carried explicitly and is NOT interchangeable with a tire card's
   * rating: `rating_display` is scored out of 10 (MessageRenderer.tsx:1272 renders
   * a literal "/10"), while `TireInfo.overallRating` is a 5-star review average.
   * Printing one on the other's scale misstates what a Tire Rack score means —
   * the same harm class as TRAC-719.
   */
  | {
      kind: 'rating';
      label?: string;
      score?: number;
      outOf: 10;
      reviewCount?: number;
      /** Sub-scores shown beneath the headline number (e.g. "Wet traction — 8.7"). */
      metrics?: { label: string; subtitle: string }[];
    }
  /**
   * A list the agent put in front of the customer — a ranked result list
   * (`ranking_cards`, numbered) or a set of options to choose between
   * (`selection_cards`). Both render as real, readable content in the chat; only
   * the click affordance is transient, and the customer's eventual pick arrives
   * separately as a `choice`.
   */
  | {
      kind: 'options';
      title?: string;
      hint?: string;
      numbered: boolean;
      items: { label: string; subtitle?: string }[];
    }
  /** A choice the customer made, so the transcript reads as a conversation. */
  | { kind: 'choice'; label: string }
  /** A narrated state change (handed to a person, survey shown). One line, not a card. */
  | { kind: 'note'; text: string }
  /** A part type this model does not know. Rendered visibly; flips completeness. */
  | { kind: 'omitted'; partType: string };

export interface ExportTurn {
  speaker: ExportSpeaker;
  at: Date;
  blocks: ExportBlock[];
}

export interface ExportCompleteness {
  status: 'complete' | 'partial';
  /** Human-readable reasons, surfaced to the customer when status is `partial`. */
  reasons: string[];
}

export interface ChatExport {
  exportedAt: Date;
  turns: ExportTurn[];
  /** Earliest/latest tire-price capture in the export; null when no tires were shown. */
  priceWindow: { earliest: Date; latest: Date } | null;
  completeness: ExportCompleteness;
}

/**
 * Part types that are real conversation content but carry no meaning in a static
 * document: buttons the customer can no longer press, in-flight indicators, and
 * internal tool chatter. Dropping these is a decision, not a gap — they do not
 * mark the export partial.
 */
const DROPPED_PART_TYPES = new Set([
  'tool',
  'agent_thinking',
  'metrics',
  // Buttons the customer can no longer press. `follow_up_actions` and
  // `routing_actions` are pure affordances with no content of their own, and
  // `ymmv_interface` is a full-screen picker, not message content.
  'follow_up_actions',
  'routing_actions',
  'ymmv_interface',
  // TRAC-848: `vehicle_info_card` and `tire_size_confirmation` are NOT rendered
  // in the conversation at all — MessageRenderer has no branch for either. They
  // are data carriers that feed the persistent VehicleHeaderBar via
  // ChatInterface's `extractVehicleFromPart`. Rendering them inline would put
  // something in the transcript the customer never saw there, so they are lifted
  // into the export's vehicle header instead (see `readVehicle`).
  'vehicle_info_card',
  'tire_size_confirmation',
]);

/**
 * Part types this model knows how to project into a block. Kept next to the
 * switch in `toBlock` and asserted against `CARD_CHUNK_TYPES` in the tests, so
 * adding a card to `chatStreamCards.ts` without teaching the export about it
 * fails CI instead of quietly punching a hole in the customer's download.
 */
const HANDLED_PART_TYPES = new Set([
  'text',
  'ranking_cards',
  'selection_cards',
  'tire_card',
  'tire_info_card',
  'tdg_link_card',
  'size_search_link_card',
  'tire_browse_card',
  'navigation_card',
  'source_cards',
  'vehicle_info_card',
  'tire_size_confirmation',
  'rating_display',
  'user_selection_card',
  'soft_handoff',
  'conversation_survey',
]);

/** True when the export can account for `partType` — by rendering it or by deliberately dropping it. */
export const isKnownPartType = (partType: string): boolean =>
  HANDLED_PART_TYPES.has(partType) || DROPPED_PART_TYPES.has(partType);

/**
 * navigation_card eyebrows, mirroring `NAV_EYEBROW` in MessageRenderer so a
 * wayfinding link is described in the export the same way it was described in the
 * chat ("Tire category", "Tire guide", …) rather than a generic "Tire Rack page".
 */
const NAV_EYEBROW: Record<string, string> = {
  category: 'Tire category',
  subcategory: 'Tire category',
  taxonomy: 'Tire category',
  brand: 'Brand',
  education: 'Tire guide',
  research: 'Tire guide',
  tool: 'Tire guide',
  parts: 'Parts & accessories',
  support: 'Help & support',
  account: 'Your account',
  shopping: 'Shop',
  shopping_entry: 'Shop',
  deals: 'Deals & rebates',
  payment: 'Financing & payment',
  legal: 'Policies',
  policy: 'Policies',
  trust: 'Policies',
  directory: 'Directory',
  company: 'About Tire Rack',
  hub: 'Tire Rack',
};

/**
 * TDG attribute weights → the labels the card shows, mirroring
 * `attributeLabels` in TDGLinkCard.
 */
const TDG_ATTRIBUTE_LABELS: Record<string, string> = {
  wethandling: 'Wet Handling',
  dryhandling: 'Dry Handling',
  ridecomfort: 'Ride Comfort',
  wear: 'Treadwear',
  price: 'Price',
  wintertraction: 'Winter Traction',
  icetraction: 'Ice Traction',
  snowtraction: 'Snow Traction',
  trailtraction: 'Trail Traction',
};

/**
 * Read the recommendation's own settings back out of its TDG URL, the way
 * TDGLinkCard does: `seasonType` drives the season label, a weight of >= 3 means
 * that priority is on, and `offRoad` / `runflat` are Y/N flags. The URL is the
 * only part of the card's state that persists, so it is the only honest source
 * for what the card said.
 */
const readTdgUrlState = (tdgUrl: string) => {
  let params: URLSearchParams;
  try {
    params = new URL(tdgUrl).searchParams;
  } catch {
    return { season: 'All-Season', priorities: [], tireTypes: [], offRoad: false, runflat: false };
  }

  const seasonType = params.get('seasonType') ?? 'allyear';
  const season =
    seasonType === 'winterOnly' ? 'Winter' : seasonType === 'summerOnly' ? 'Summer' : 'All-Season';
  const offRoad = params.get('offRoad') === 'Y';

  // Which chips the card would have shown, per TDGLinkCard's `visibleAttributes`.
  const base = ['wethandling', 'dryhandling', 'ridecomfort', 'wear', 'price'];
  const visible =
    seasonType === 'winterOnly'
      ? ['icetraction', 'snowtraction', ...base]
      : seasonType === 'allyear'
        ? [...base, 'wintertraction', ...(offRoad ? ['trailtraction'] : [])]
        : [...base, ...(offRoad ? ['trailtraction'] : [])];

  return {
    season,
    priorities: visible.map((key) => ({
      label: TDG_ATTRIBUTE_LABELS[key],
      on: parseInt(params.get(key) ?? '0', 10) >= 3,
    })),
    tireTypes: [
      { label: 'All-Season', on: seasonType === 'allyear' },
      { label: 'Summer Only', on: seasonType === 'summerOnly' },
      { label: 'Winter Only', on: seasonType === 'winterOnly' },
    ],
    offRoad,
    runflat: params.get('runflat') === 'Y',
  };
};

const asDate = (value: unknown, fallback: Date): Date => {
  const d = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(d.getTime()) ? fallback : d;
};

const toTireSnapshot = (tire: TireInfo, capturedAt: Date): TireSnapshot => ({
  manufacturer: tire.manufacturer,
  modelName: tire.modelName,
  tireType: tire.tireType,
  productUrl: tire.productUrl,
  price: tire.price || tire.priceRange,
  priceKind: tire.price ? 'exact' : tire.priceRange ? 'range' : 'unavailable',
  overallRating: tire.overallRating,
  reviewCount: tire.reviewCount,
  ratingMetrics: tire.ratingMetrics,
  specs: tire.specs,
  capturedAt,
});

/**
 * Project one part into a block. Returns `null` for deliberately dropped types.
 * Anything unrecognized falls through to an `omitted` block — the caller treats
 * that as a coverage hole.
 */
const toBlock = (part: MessagePart, at: Date): ExportBlock | null => {
  if (DROPPED_PART_TYPES.has(part.type)) return null;

  switch (part.type) {
    case 'text':
      return part.content?.trim() ? { kind: 'prose', text: part.content } : null;

    case 'tire_card':
    case 'tire_info_card':
      return part.tire
        ? { kind: 'tires', tires: [toTireSnapshot(part.tire, at)] }
        : { kind: 'omitted', partType: part.type };

    case 'tdg_link_card': {
      // The recommendations card. In the chat it also carries an interactive
      // priorities/tire-type picker — transient, so dropped — but the vehicle,
      // size and season it was built FOR are what make the saved link meaningful
      // later, so they ride along as the blurb. Payload shape per the agent's
      // `_tdg_card_frontend` (agentv2/cards.py).
      if (!part.tdgUrl) return { kind: 'omitted', partType: part.type };
      const v = part.vehicle;
      const size =
        v?.frontTireSize && v?.rearTireSize
          ? `F: ${v.frontTireSize}   R: ${v.rearTireSize}`
          : v?.tireSize;
      const url = readTdgUrlState(part.tdgUrl);
      return {
        kind: 'recommendations',
        title: part.title || 'Your Personalized Tire Recommendations',
        vehicle: v?.description,
        details: [
          size ? { label: 'Size', value: size } : null,
          // The URL's seasonType is authoritative over the payload's label — it is
          // what the link actually asks TDG for.
          { label: 'Season', value: url.season ?? v?.season ?? 'All-Season' },
          url.offRoad ? { label: 'Off-Road', value: 'Yes' } : null,
          url.runflat ? { label: 'Run-flat', value: 'Yes' } : null,
        ].filter(Boolean) as { label: string; value: string }[],
        priorities: url.priorities,
        tireTypes: url.tireTypes,
        indicators: (part.indicators ?? [])
          .filter((i) => !['offroad', 'staggered', 'runflat'].includes(i.type))
          .map((i) => ({ label: i.label, description: i.description })),
        url: part.tdgUrl,
        // The card's own button copy, not the payload's `ctaText` — TDGLinkCard
        // hardcodes this string for the main link.
        cta: 'Recommendations for customers like you →',
      };
    }

    case 'size_search_link_card':
    case 'tire_browse_card': {
      if (!part.searchUrl) return { kind: 'omitted', partType: part.type };
      // Mirrors TireBrowseCard: an explicit title wins over the size, and the
      // eyebrow distinguishes a filtered search from a plain size lookup.
      const staggered = !part.title && part.isStaggered && part.frontTireSize && part.rearTireSize;
      const label = staggered
        ? `Front ${part.frontTireSize}, rear ${part.rearTireSize}`
        : part.title || part.tireSize || 'Tire search';
      return {
        kind: 'link',
        label,
        url: part.searchUrl,
        eyebrow: part.title ? 'Tire search' : staggered ? 'Search by size — staggered' : 'Search by size',
        cta: part.ctaText || (staggered ? 'Browse staggered tires →' : 'Browse tires →'),
      };
    }

    case 'navigation_card':
      return part.url
        ? {
            kind: 'link',
            label: part.title || 'Tire Rack',
            url: part.url,
            eyebrow: NAV_EYEBROW[(part.navType || '').toLowerCase()] || 'Tire Rack page',
            cta: 'Visit page →',
            blurb: part.whenToUse,
          }
        : { kind: 'omitted', partType: part.type };

    case 'source_cards': {
      const items = (part.options ?? [])
        .filter((o) => o.label && o.value)
        .map((o) => ({ label: o.label, url: o.value, kind: o.subtitle }));
      return items.length ? { kind: 'sources', title: part.title, items } : null;
    }

    case 'ranking_cards':
    case 'selection_cards': {
      const items = (part.options ?? [])
        .filter((o) => o.label)
        .map((o) => ({ label: o.label, subtitle: o.subtitle }));
      return items.length
        ? {
            kind: 'options',
            title: part.title,
            hint: part.hint,
            numbered: part.type === 'ranking_cards',
            items,
          }
        : null;
    }

    case 'rating_display':
      return {
        kind: 'rating',
        label: part.title,
        score: part.overallRating,
        outOf: 10,
        reviewCount: part.reviewCount,
        metrics: part.metrics,
      };

    case 'user_selection_card':
      return { kind: 'choice', label: part.selectedLabel || part.selectedValue || '' };

    case 'soft_handoff':
      return { kind: 'note', text: `Handed off to ${part.department || 'a Tire Rack specialist'}.` };

    case 'conversation_survey': {
      // The customer's own answers persist on the part (TRAC-732 `surveyState`)
      // so the card rehydrates. It is their data in their download — recording
      // "a survey appeared" while dropping what they said would be a gap.
      const s = part.surveyState;
      const answers = [
        typeof s?.csat === 'number' ? `rated this chat ${s.csat} out of 5` : null,
        typeof s?.resolved === 'boolean'
          ? s.resolved
            ? 'said it answered your question'
            : 'said it did not answer your question'
          : null,
        s?.feedback ? `commented: “${s.feedback}”` : null,
      ].filter(Boolean);
      return {
        kind: 'note',
        text: answers.length ? `You ${answers.join(', ')}.` : 'Feedback survey shown.',
      };
    }

    default:
      return { kind: 'omitted', partType: part.type };
  }
};

/** Coalesce adjacent `tires` blocks so a presented set reads as one set. */
const coalesceTires = (blocks: ExportBlock[]): ExportBlock[] =>
  blocks.reduce<ExportBlock[]>((acc, block) => {
    const last = acc[acc.length - 1];
    if (block.kind === 'tires' && last?.kind === 'tires') {
      last.tires.push(...block.tires);
      return acc;
    }
    acc.push(block);
    return acc;
  }, []);

const speakerFor = (message: Message): ExportSpeaker => {
  if (message.role === 'user') return 'customer';
  if (message.responder === 'human') return 'human-agent';
  if (message.responder === 'system' || message.isSystemMarker) return 'system';
  return 'traci';
};

export interface BuildExportOptions {
  /** Injected so the caller controls the clock (and tests stay deterministic). */
  now: Date;
  /**
   * Set when the caller knows persistence failed for this conversation — e.g. a
   * localStorage quota error was observed. Forces `partial` with that reason,
   * because a reloaded conversation cannot detect its own truncation.
   */
  knownPersistenceGap?: string;
}

/**
 * Build the export model from the live message array.
 *
 * Skips UI scaffolding — the welcome message and session-expiry notices — so the
 * export reads as the conversation the customer actually had. The welcome is
 * dropped even once committed: its text ends "try one of the options below:" and
 * the options are starter-prompt buttons, which are correctly dropped as
 * transient. Keeping the prose alone leaves the export opening on a promise it
 * does not keep.
 */
export function buildChatExport(messages: Message[], options: BuildExportOptions): ChatExport {
  const { now, knownPersistenceGap } = options;
  const reasons: string[] = [];
  const omittedTypes = new Set<string>();
  const turns: ExportTurn[] = [];
  let earliest: Date | null = null;
  let latest: Date | null = null;

  for (const message of messages) {
    if (message.isSessionExpiry) continue;
    if (message.isCommittedWelcome || message.kind === 'welcome') continue;
    if (message.isClientOnly) continue;

    const at = asDate(message.timestamp, now);
    const blocks: ExportBlock[] = [];

    // A message with parts carries its prose inside them; only fall back to
    // `content` when there are none, or the text would be duplicated.
    const parts = message.parts ?? [];
    if (parts.length === 0) {
      const text = (message.displayContent ?? message.content)?.trim();
      if (text) blocks.push({ kind: 'prose', text });
    } else {
      for (const part of parts) {
        const block = toBlock(part, at);
        if (!block) continue;
        if (block.kind === 'omitted') omittedTypes.add(block.partType);
        if (block.kind === 'tires') {
          for (const tire of block.tires) {
            if (!earliest || tire.capturedAt < earliest) earliest = tire.capturedAt;
            if (!latest || tire.capturedAt > latest) latest = tire.capturedAt;
          }
        }
        blocks.push(block);
      }
    }

    if (blocks.length === 0) continue;
    turns.push({ speaker: speakerFor(message), at, blocks: coalesceTires(blocks) });
  }

  if (omittedTypes.size > 0) {
    reasons.push(
      `This export does not yet know how to include: ${[...omittedTypes].sort().join(', ')}.`,
    );
  }
  if (knownPersistenceGap) reasons.push(knownPersistenceGap);

  return {
    exportedAt: now,
    turns,
    priceWindow: earliest && latest ? { earliest, latest } : null,
    completeness: {
      status: reasons.length > 0 ? 'partial' : 'complete',
      reasons,
    },
  };
}
